import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import { prefersReducedMotion } from "../../lib/gsap";

export default function SplitTextReveal({
  children,
  as: Tag = "span",
  type = "words",
  immediate = false,
  delay = 0,
  duration = 0.75,
  stagger = 0.045,
  className = "",
  start = "top 85%",
}) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !children) return undefined;

    let split;

    const ctx = gsap.context(() => {
      if (prefersReducedMotion()) {
        gsap.set(el, { opacity: 1, clearProps: "transform" });
        return;
      }

      split = new SplitText(el, {
        type,
        wordsClass: "gsap-split-word",
        charsClass: "gsap-split-char",
        linesClass: "gsap-split-line",
      });

      const targets = type.includes("chars")
        ? split.chars
        : type.includes("lines")
          ? split.lines
          : split.words;

      const usesGradient = className.includes("saas-gradient-text");
      if (usesGradient) {
        el.classList.remove("saas-gradient-text");
        targets.forEach((target) => target.classList.add("saas-gradient-text"));
      }

      gsap.set(targets, {
        opacity: 0,
        y: 32,
        rotateX: -28,
        transformOrigin: "50% 100%",
      });

      const play = () => {
        gsap.to(targets, {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration,
          delay,
          stagger,
          ease: "power3.out",
        });
      };

      if (immediate) {
        play();
      } else {
        ScrollTrigger.create({
          trigger: el,
          start,
          once: true,
          onEnter: play,
        });
      }
    }, el);

    return () => {
      split?.revert();
      ctx.revert();
    };
  }, [children, type, immediate, delay, duration, stagger, start, className]);

  return (
    <Tag ref={ref} className={className}>
      {children}
    </Tag>
  );
}
